import React, { useState, useEffect, useMemo, useRef } from 'react';
import './WellChart.css';
import { fetchColumnsData } from '../dispatchers/ColumnSelectorDispatcher';
import { useAppDispatch, useAppSelector } from '../hooks/storeHooks';
import { DashboardConfig, saveUserPerferences } from '../dispatchers/UserPreferenceDispatcher';
import SuccessfullToast from '../features/common/toast/SuccessfullToast';
import ErrorToast from '../features/common/toast/ErrorToast';
import Dashboard from '../components/time-series-chart/Dashboard';
import OilGasChart from '../components/oil-gas-liquid-chart/OilGasChart';
import 'react-grid-layout/css/styles.css';
import { Layout } from 'react-grid-layout';
import RGL, { WidthProvider } from 'react-grid-layout';
import TaskDetailsWidget from '../components/task-details-widget/TaskDetailsWidget';
import { updateTaskTable } from '../components/slices/ColumnSelectorSlice';
import LastFiveShutdownWidget from '../components/last-five-shutdowns/LastFiveShutdownsWidget';
import { XYChart } from '@amcharts/amcharts5/xy';
import RodLiftStatusOverView from '../components/rod-lift-status-overview-table/RodLiftStatusOverView';
import RodLiftWellStatusRegisterWidget from '../components/rod-lift-register-well-status-table/RodLiftWellStatusRegisterWidget';
import MotorPowerWidget from '../components/widget-tables/MotorPowerWidget';
import { useParams } from 'react-router-dom';
import { fetchWellTestId } from '../dispatchers/WellTestDispatcher';
import {
  fetchLatestAssetTrendDataAsync,
  setInitialState as clearLatestAssetTrendData,
} from '../components/slices/LatestAssetTrendDataSlice';
import { ESP_CHARTS_PARAM_STD_TYPES, GL_CHARTS_PARAM_STD_TYPES } from '../constants/latest-asset-trend.constant';
import WellEquipmentStatusWidget from '../components/widget-tables/WellEquipmentStatusWidget';
import SurfacePowerOutputWidget from '../components/widget-tables/SurfacePowerOutputWidget';
import VdsSettingsProtectionWidget from '../components/widget-tables/VdsSettingsProtectionWidget';
import { calculateUpdatedLayout } from './WidgetLayoutService';
import { fetchPumpCheckerEvents } from '../dispatchers/EventsDispatcher';
// import { setInitialState } from '../components/slices/WellTestTableSlice';
import CardViewer from '../components/card-viewer/CardViewer';
// import { setWellTestIdInitialState } from '../components/slices/WellTestIdSlice';
import WellTestTableWidget from '../well-test-table/WellTestTableWidget';
import Loader from '../features/common/Loader/Loader';
import GlWellEquipmentStatusWidget from '../components/widget-tables/GlWellEquipmentStatusWidget';
import { fetchLicenseAccessDetails } from '../dispatchers/WidgetAccessDispatcher';
import NoAccessWidget from '../components/no-access-widget/NoAccessWidget';
import { AccessiableWidget } from '../models/LicenseWidget';
import useConfig from '../hooks/useConfig';
import { fetchDefaultWellTestDataAsync } from '../dispatchers/DefaultWellTestDispatcher';
import { R } from '../rbac';
import { ProtectComponent } from '../rbac';
import Header from '../components/time-series-chart/Header';
import { setInitialState as clearTestWellData } from '../components/slices/WellTestTableSlice';
import { setInitialState as clearPumpCheckerEvents } from '../components/slices/PumpCheckerEventsSlice';
import { setDefaultWellTestInitialState as clearDefaultWellTest } from '../components/slices/DefaultWellTestFetchSlice';
import { setWellTestIdInitialState } from '../components/slices/WellTestIdSlice';
import { clearEvents as clearWellNoteEvents } from '../components/slices/WellNoteSlice';

const GridLayout = WidthProvider(RGL);

export default function Main() {
  const chartType = useAppSelector((state) => state.AssetAnalysis.chartType);
  const isGLDashboard = chartType?.toLowerCase().includes('gl');
  const isRLDashboard = chartType?.toLowerCase().includes('rl');
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [isAddNoteWell, setIsAddNoteWell] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const [isEditYAxisOpen, setIsEditYAxisOpen] = useState<boolean>(false);

  // State for sticky header behavior
  // const [isSticky, setIsSticky] = useState(false);
  // const [headerHeight, setHeaderHeight] = useState(0);
  // const [headerOffsetTop, setHeaderOffsetTop] = useState(0);
  const headerRef = useRef<HTMLDivElement>(null);

  // const [scrollY, setScrollY] = useState(0);

  // Function for Y-axis refresh (not implemented but needed for Header interface)
  const setYAxisRefreshVersion = (version: React.SetStateAction<number>) => {
    console.log('Y-axis refresh version:', version);
  };

  // Permission object for Header component
  const rPermission: { [key: string]: boolean } = {};

  // Shared chart refs for syncing between TimeseriesChart and CardViewer Contextualizers
  const sharedChartRefs = useRef<(XYChart | null)[]>([]);

  // Ref to track the success message timeout for cleanup
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Callback functions for components to register their charts
  const registerTimeSeriesChart = (index: number, chart: XYChart) => {
    // TimeseriesCharts use indices 0-9
    if (chart && index >= 0 && index < 10) {
      sharedChartRefs.current[index] = chart;
    }
  };

  const registerContextualizerChart = (index: number, chart: XYChart) => {
    // Contextualizer charts use indices 10-19 to avoid conflicts
    if (chart && index >= 0 && index < 10) {
      sharedChartRefs.current[10 + index] = chart;
    }
  };

  const dispatch = useAppDispatch();
  const licenseAccessDetails = useAppSelector((state: any) => state.widgetAccessSlice.data);
  const pumpCheckerPageData = useAppSelector((state: any) => state.testWellColumnsSlice.data?.widgets);
  const chartDataCounts = useAppSelector((state) => state.widgetLayout.chartDataCounts);
  const { enablePumpChecker, enableLicenseCheck } = useConfig();
  const effectivePumpChecker = enablePumpChecker && !isRLDashboard;
  const wellTestWidget = effectivePumpChecker
    ? pumpCheckerPageData?.find((obj: any) => obj.key === 'wellTestTable')
    : pumpCheckerPageData?.find((obj: any) => obj.key === 'defaultWellTestTable');
  const timeseriesChartData = pumpCheckerPageData?.find((obj: any) => obj.key === 'timeseriesChart');
  const columnObjects = wellTestWidget?.widgetProperty?.widgetProperties?.columns || [];
  const { assetId } = useParams<{ assetId: string }>();

  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [saveConfigResponse, setSaveConfigResponse] = useState(false);

  const preferenceData = useAppSelector((state: any) => state.testWellColumnsSlice.data?.widgets);
  const timeSeriesData = preferenceData?.find((item: any) => item.key === 'timeseriesChart');
  const defaultChartType = timeSeriesData?.widgetProperty?.widgetProperties?.chartType;
  const [layout, setLayout] = useState(defaultChartType || 'Stacked');

  const chartViewType = useAppSelector(
    (state) =>
      state.testWellColumnsSlice.data?.widgets?.find((w: any) => w.key === 'timeseriesChart')?.widgetProperty
        ?.widgetProperties?.chartType,
  );

  useEffect(() => {
    if (chartViewType && layout !== chartViewType) {
      setLayout(chartViewType);
    }
  }, [chartViewType, layout]);

  const testWellData = useAppSelector((state: any) => state.testWellDataSlice.data);
  const t1WellTestData = useAppSelector((state: any) => state.DefaultWellTest.data);
  const wellId = useAppSelector((state) => state.WellTestIdSlice?.data?.WellId);
  const wellTestIdData = useAppSelector((state: any) => state.WellTestIdSlice?.data);
  const endDate = useAppSelector((state: any) => state.AssetTrendDataSlice.endDate);
  const [isAddTestModalOpen, setIsAddTestModalOpen] = useState(false);
  const wellTestObject = pumpCheckerPageData;

  const sortedColumnObjects = useMemo(() => {
    return [...columnObjects].sort((a, b) => a.order - b.order);
  }, [columnObjects]);
  const [taskRefreshTrigger, setTaskRefreshTrigger] = useState(false);

  const dataTableRef = useRef<HTMLDivElement>(null);

  // Reset all local state variables when assetId changes
  useEffect(() => {
    if (assetId) {
      // IMMEDIATELY clear Redux state to prevent flickering with old data
      dispatch(clearTestWellData());
      dispatch(clearLatestAssetTrendData());
      dispatch(clearPumpCheckerEvents());
      dispatch(clearDefaultWellTest());
      dispatch(clearWellNoteEvents());
      dispatch(setWellTestIdInitialState());

      // Reset modal states
      setIsAddTaskModalOpen(false);
      setIsAddNoteWell(false);
      setIsAddTestModalOpen(false);
      setIsEditYAxisOpen(false);

      // Reset loading state
      setIsLoading(true);

      // Reset scroll position
      //setScrollY(0);

      // Reset messages
      setSuccessMessage('');
      setErrorMessage('');
      setSaveConfigResponse(false);

      // Reset task refresh trigger
      setTaskRefreshTrigger(false);

      // Reset layout to default (will be updated from preferences)
      setLayout('Stacked');

      // Clear all chart refs
      sharedChartRefs.current.forEach((chart) => {
        if (chart && typeof chart.dispose === 'function') {
          try {
            chart.dispose();
          } catch (error) {
            console.warn('Error disposing chart on asset change:', error);
          }
        }
      });
      sharedChartRefs.current = [];

      // Clear timeout if exists
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }

      console.log(`🔄 Reset all variables for new asset: ${assetId}`);
    }
  }, [assetId, dispatch]);

  // First useEffect: Handle chartType changes
  useEffect(() => {
    let isActive = true;
    const fetchChartTypeData = async () => {
      if (!chartType) return;

      await dispatch(fetchLicenseAccessDetails());
      if (!isActive) return;
      if ((chartType === 'rl-well-charts' || chartType === 'facility-well-charts') && assetId) {
        await dispatch(fetchColumnsData({ chartType, assetGUID: assetId }));
      } else {
        await dispatch(fetchColumnsData(chartType));
      }
    };
    fetchChartTypeData();
    return () => {
      isActive = false;
    };
  }, [chartType, assetId, dispatch]);

  // Second useEffect: Handle assetId changes after chartType is set
  useEffect(() => {
    let isActive = true;
    const fetchAssetData = async () => {
      if (!assetId || !chartType) return;

      setIsLoading(true);

      await dispatch(fetchWellTestId(assetId));
      if (!isActive) return;

      await dispatch(
        fetchLatestAssetTrendDataAsync({
          assetId: String(assetId),
          ParamStdTypes: isGLDashboard ? GL_CHARTS_PARAM_STD_TYPES : ESP_CHARTS_PARAM_STD_TYPES,
        }),
      );
      if (!isActive) return;

      if (!effectivePumpChecker) {
        await dispatch(fetchDefaultWellTestDataAsync(assetId));
      }

      if (isActive) setIsLoading(false);
    };
    fetchAssetData();
    return () => {
      isActive = false;
    };
  }, [assetId, chartType, isGLDashboard, effectivePumpChecker, dispatch]);

  interface GridLayoutItem {
    i: string;
    x: number;
    y: number;
    w: number;
    h: number;
    minH?: number;
    minW?: number;
    moduleId?: number;
  }

  const safeLayout = (wellTestObject: any[]): Layout[] => {
    const updatedLayout = calculateUpdatedLayout(wellTestObject, chartDataCounts);
    return updatedLayout
      ?.map((obj: any, index: number): GridLayoutItem | null => {
        const lg = obj?.layout?.lg;
        if (!lg || !obj?.key) {
          console.warn(`Invalid layout at index ${index}`, obj);
          return null;
        }

        if (obj.key === 'defaultWellTestTable' && effectivePumpChecker) {
          return null; // Skip rendering Tier1 WellTest for Tier2 Users
        }
        if (obj.key === 'wellTestTable' && !effectivePumpChecker) {
          return null; // Skip rendering Tier2 WellTest for Tier1 Users
        }
        // TODO: Remove after widget property is updated to reflect new height for Card Viewer
        // if (obj.key === 'cardViewer') {
        //   const item = {
        //     i: String(obj.key),
        //     x: lg.x,
        //     y: lg.y,
        //     w: lg.width,
        //     h: 28,
        //     minH: lg.minH ?? 1,
        //     minW: lg.minW ?? 6,
        //     moduleId: obj.widgetProperty?.moduleId,
        //   };

        //   return item;
        // }
        const item: GridLayoutItem = {
          i: String(obj.key),
          x: lg.x,
          y: lg.y,
          w: lg.width,
          h: lg.height,
          minH: lg.minH ?? 1,
          minW: lg.minW ?? 6,
          moduleId: obj.widgetProperty?.moduleId,
        };

        return item;
      })
      .filter((item): item is GridLayoutItem => item !== null);
  };

  const mamonewEndDate = useMemo(() => {
    if (endDate) {
      const newEndDate = new Date(endDate);
      newEndDate.setUTCHours(5, 0, 0, 0);

      const nextDate = new Date(newEndDate);
      nextDate.setUTCDate(newEndDate.getUTCDate() + 1);

      const formattedDate = nextDate.toISOString();
      return { newEndDate, formattedDate };
    }

    return null;
  }, [endDate]);

  useMemo(() => {
    if (mamonewEndDate && wellTestIdData?.WellId) {
      dispatch(
        fetchPumpCheckerEvents({
          wellId: wellTestIdData.WellId,
          startDate: mamonewEndDate.newEndDate.toISOString(),
          endDate: mamonewEndDate.formattedDate,
        }),
      );
    }
  }, [endDate, wellTestIdData]);

  const layoutMemo = useMemo(() => safeLayout(wellTestObject), [wellTestObject, chartDataCounts]);

  useEffect(() => {
    // Setup: Add scroll event listener to track vertical scroll position
    // const handleScroll = () => {
    //   const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    //   setScrollY(scrollTop);
    // };
    // window.addEventListener('scroll', handleScroll);

    return () => {
      // window.removeEventListener('scroll', handleScroll);

      sharedChartRefs.current.forEach((chart) => {
        if (chart && typeof chart.dispose === 'function') {
          try {
            chart.dispose();
          } catch (error) {
            console.warn('Error disposing AmChart instance:', error);
          }
        }
      });
      sharedChartRefs.current = [];

      try {
        dispatch(clearTestWellData());
        dispatch(clearLatestAssetTrendData());
        dispatch(clearPumpCheckerEvents());
        dispatch(clearDefaultWellTest());
        dispatch(clearWellNoteEvents());
        dispatch(setWellTestIdInitialState());
      } catch (error) {
        console.warn('Error clearing Redux state:', error);
      }

      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  // useEffect(() => {
  //   const fetchData = async () => {
  //     await dispatch(fetchLicenseAccessDetails());
  //     if (chartType === 'rl-well-charts' && assetId) {
  //       await dispatch(fetchColumnsData({ chartType, assetGUID: assetId }));
  //     } else {
  //       await dispatch(fetchColumnsData(chartType));
  //     }
  //     setIsLoading(false);
  //   };

  //   fetchData();
  // }, [dispatch, chartType, assetId]);

  const [columnObjectsState, setColumnObjectsState] = useState(columnObjects);

  useEffect(() => {
    if (JSON.stringify(columnObjects) !== JSON.stringify(columnObjectsState)) {
      setColumnObjectsState(columnObjects);
    }
  }, [columnObjects]);

  function updateColumns(preferences: any, columns: any) {
    preferences.forEach((pref: any) => {
      const columnIndex = columns.findIndex((col: any) => col.key === pref.Key);
      if (columnIndex !== -1) {
        columns[columnIndex].selected = pref.Selected;
        columns[columnIndex].order = pref.Order;
      }
    });
    columns.sort((a: any, b: any) => a.order - b.order);
  }

  const handleApplyClicked = async (source: string, columns: any, isConfigurationChecked: boolean) => {
    if (source === 'taskTable') {
      const taskDetails = pumpCheckerPageData?.find((obj: any) => obj.key === 'taskDetails');
      if (!taskDetails) return;
      if (taskDetails) {
        try {
          const orderedKeys: string[] = columns && columns.length > 0 ? columns.map((obj: any) => obj.key) : [];

          const allSortedColumns = [...columns].sort((a, b) => {
            const indexA = orderedKeys.indexOf(a.key);
            const indexB = orderedKeys.indexOf(b.key);
            return (indexA !== -1 ? indexA : Infinity) - (indexB !== -1 ? indexB : Infinity);
          });

          const visibleKeys = columns.filter((obj: any) => obj.selected).map((obj: any) => obj.key);
          const updatedColumns = allSortedColumns.map((col, index) => ({
            Order: index + 1,
            Label: col.label,
            Key: col.key,
            Selected: visibleKeys ? visibleKeys.includes(col.key) : [],
            Visible: true,
          }));
          const transformedTaskTableWidget: DashboardConfig = {
            DashboardName: taskDetails?.widgetProperty?.dashboard ?? '',
            WidgetName: taskDetails?.widgetProperty?.key ?? '',
            PropertyType: 'Columns',
            Preferences: updatedColumns,
          };
          if (isConfigurationChecked) {
            const resultAction = await dispatch(saveUserPerferences(transformedTaskTableWidget));
            if (typeof resultAction.payload === 'boolean') {
              if (taskDetails?.widgetProperty && taskDetails?.widgetProperty?.isDefault) {
                const clonedtaskTableWidget = JSON.parse(JSON.stringify(taskDetails));

                clonedtaskTableWidget.widgetProperty.isDefault = false;

                updateColumns(updatedColumns, clonedtaskTableWidget.widgetProperty.widgetProperties.columns);
                dispatch(updateTaskTable(clonedtaskTableWidget));
              }

              setSaveConfigResponse(resultAction.payload);
              if (resultAction.payload) {
                setSuccessMessage('Successfully Saved Task Table Configuration');
                successTimeoutRef.current = setTimeout(() => {
                  setSaveConfigResponse(false);
                }, 3000);
              } else {
                setErrorMessage('Error saving user preferences.');
              }
            } else {
              setSaveConfigResponse(false);
              setErrorMessage('Unexpected response from the server.');
            }
          }
        } catch (error) {
          console.error('Unexpected error during dispatch:', error);
          setErrorMessage('Unexpected error occurred. Please try again saving user preference.');
        }
      }
    }
  };

  // Render a loading spinner or message while data is being fetched
  if (isLoading) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  const handleOpenAddTaskModal = (status: boolean) => {
    setIsAddTaskModalOpen(status);
  };

  const handleOpenAddWellNoteModal = (status: boolean) => {
    setIsAddNoteWell(status);
  };

  const handleTaskSaved = () => {
    setIsAddTaskModalOpen(false);
    setTaskRefreshTrigger((prev) => !prev);
  };

  const isWidgetAccessible = (moduleId: number): boolean => {
    if (!enableLicenseCheck) return true;
    return licenseAccessDetails?.features?.some((x: AccessiableWidget) => x.moduleId === moduleId);
  };

  return (
    <div className='chart-component-container main-container well-chart-component-container'>
      <SuccessfullToast
        isShowEnabled={saveConfigResponse}
        message={successMessage}
        callback={() => setSuccessMessage('')}
      />
      <ErrorToast isShowEnabled={!!errorMessage} message={errorMessage} callback={() => setErrorMessage('')} />
      {/* <div className='scroll-indicator-box'></div> */}
      <div ref={headerRef} className={`header-container fix-nav-bar-charts fix-nav-bar-charts-sticky`}>
        <div className='scroll-indicator-box'></div>
        <Header
          accessible={true}
          timeseriesChartData={timeseriesChartData}
          retrieveMinMax={layout === 'Stacked'}
          openAddTaskModal={handleOpenAddTaskModal}
          fetchCov={layout === 'COV'}
          aggegateMethod='last'
          openAddNoteModal={handleOpenAddWellNoteModal}
          layout={layout}
          isEditYAxisOpen={isEditYAxisOpen}
          setIsEditYAxisOpen={setIsEditYAxisOpen}
          setYAxisRefreshVersion={setYAxisRefreshVersion}
          disableViewControls={true}
          enableRefreshOnTimeRangeSelector={true}
          rPermission={rPermission}
        />
        {/* <hr /> */}
      </div>
      {/* <div
        className='content-area'
        style={{
          transition: 'margin-top 0.3s ease-in-out',
        }}
      > */}
      <GridLayout
        layout={layoutMemo}
        cols={12}
        rowHeight={20}
        isDraggable={false}
        isResizable={true}
        className='layout-react-grid well-chart-grid'
        useCSSTransforms={false}
        isDroppable={false}
        margin={[23, 23]}
      >
        {layoutMemo?.map((item: any) => (
          <div key={item.i} id={`grid-item-${item.i}`} className='grid-item' style={{ display: 'block' }}>
            {item.i === 'timeseriesChart' && (
              <ProtectComponent resource={R.widget(chartType, item.i)} action='view' widgetName='Time Series Chart'>
                <Dashboard
                  timeseriesChartData={timeseriesChartData}
                  openAddTaskModal={handleOpenAddTaskModal}
                  openAddNoteModal={handleOpenAddWellNoteModal}
                  layout={layout}
                  chartType={chartType}
                  accessible={isWidgetAccessible(item.moduleId)}
                  widgetName={item.i}
                  externalChartRefs={sharedChartRefs}
                  onChartRegistered={registerTimeSeriesChart}
                />
              </ProtectComponent>
            )}
            {item.i === 'oilWaterGasChart' &&
              (isWidgetAccessible(item.moduleId) ? (
                <ProtectComponent
                  resource={R.widget(chartType, item.i)}
                  action='view'
                  widgetName='Oil, gas, water, total liquids'
                >
                  <div className='main-container'>
                    <OilGasChart widgetName={item.i} />
                  </div>
                </ProtectComponent>
              ) : (
                <NoAccessWidget widgetName='Oil, gas, water, total liquids' />
              ))}
            {((item.i === 'defaultWellTestTable' && !effectivePumpChecker) ||
              (item.i === 'wellTestTable' && effectivePumpChecker)) &&
              (isWidgetAccessible(item.moduleId) ? (
                <ProtectComponent resource={R.widget(chartType, item.i)} action='view' widgetName='Well Tests'>
                  <main className='main-container table-wrap'>
                    <WellTestTableWidget
                      wellId={wellId}
                      wellTestIdData={wellTestIdData}
                      isAddTestModalOpen={isAddTestModalOpen}
                      setIsAddTestModalOpen={setIsAddTestModalOpen}
                      isAddTaskModalOpen={isAddTaskModalOpen}
                      setIsAddTaskModalOpen={setIsAddTaskModalOpen}
                      isAddNoteWell={isAddNoteWell}
                      setIsAddNoteWell={setIsAddNoteWell}
                      sortedColumnObjects={sortedColumnObjects}
                      dataTableRef={dataTableRef}
                      columnObjects={columnObjects}
                      wellTestWidget={wellTestWidget}
                      assetId={assetId}
                      testWellData={effectivePumpChecker ? testWellData : t1WellTestData?.values || []}
                      handleTaskSaved={handleTaskSaved}
                      setSaveConfigResponse={setSaveConfigResponse}
                      setSuccessMessage={setSuccessMessage}
                      setErrorMessage={setErrorMessage}
                      widgetName={item.i}
                    />
                  </main>
                </ProtectComponent>
              ) : (
                <NoAccessWidget widgetName='Well tests' />
              ))}
            {item.i === 'taskDetails' &&
              (isWidgetAccessible(item.moduleId) ? (
                <ProtectComponent resource={R.widget(chartType, item.i)} action='view' widgetName='Task details'>
                  <div>
                    <TaskDetailsWidget
                      wellId={wellId}
                      dashboardName={chartType}
                      taskRefreshTrigger={taskRefreshTrigger}
                      handleApplyClickedTaskWidget={(columns: any, isConfigurationChecked: boolean) =>
                        handleApplyClicked('taskTable', columns, isConfigurationChecked)
                      }
                    />
                  </div>
                </ProtectComponent>
              ) : (
                <NoAccessWidget widgetName='Task details' />
              ))}
            {item.i === 'last5Shutdowns' &&
              (isWidgetAccessible(item.moduleId) ? (
                <ProtectComponent resource={R.widget(chartType, item.i)} action='view' widgetName='Last 5 shutdowns'>
                  <div>
                    <LastFiveShutdownWidget />
                  </div>
                </ProtectComponent>
              ) : (
                <NoAccessWidget widgetName='Last 5 shutdowns' />
              ))}
            {item.i === 'rl-statusRegister' &&
              (isWidgetAccessible(item.moduleId) ? (
                <ProtectComponent resource={R.widget(chartType, item.i)} action='view' widgetName='Bufflehead well VFD'>
                  <RodLiftWellStatusRegisterWidget />
                </ProtectComponent>
              ) : (
                <NoAccessWidget widgetName='Bufflehead well VFD' />
              ))}
            {item.i === 'rl-wellStatus' &&
              (isWidgetAccessible(item.moduleId) ? (
                <ProtectComponent resource={R.widget(chartType, item.i)} action='view' widgetName='Status registers'>
                  <RodLiftStatusOverView />
                </ProtectComponent>
              ) : (
                <NoAccessWidget widgetName='Status registers' />
              ))}
            {item.i === 'motorPower' &&
              (isWidgetAccessible(item.moduleId) ? (
                <ProtectComponent resource={R.widget(chartType, item.i)} action='view' widgetName='Motor power'>
                  <MotorPowerWidget />
                </ProtectComponent>
              ) : (
                <NoAccessWidget widgetName='Motor power' />
              ))}
            {item.i === 'esp-wellStatus' &&
              (isWidgetAccessible(item.moduleId) ? (
                <ProtectComponent resource={R.widget(chartType, item.i)} action='view' widgetName='Status'>
                  <WellEquipmentStatusWidget />
                </ProtectComponent>
              ) : (
                <NoAccessWidget widgetName='Status' />
              ))}
            {item.i === 'surfacePowerOutput' &&
              (isWidgetAccessible(item.moduleId) ? (
                <ProtectComponent
                  resource={R.widget(chartType, item.i)}
                  action='view'
                  widgetName='Surface power output'
                >
                  <SurfacePowerOutputWidget />
                </ProtectComponent>
              ) : (
                <NoAccessWidget widgetName='Surface power output' />
              ))}
            {item.i === 'vsdSettingProtection' &&
              (isWidgetAccessible(item.moduleId) ? (
                <ProtectComponent
                  resource={R.widget(chartType, item.i)}
                  action='view'
                  widgetName='VSD settings & protection'
                >
                  <VdsSettingsProtectionWidget />
                </ProtectComponent>
              ) : (
                <NoAccessWidget widgetName='VSD settings & protection' />
              ))}
            {item.i === 'gl-wellStatus' &&
              (isWidgetAccessible(item.moduleId) ? (
                <ProtectComponent resource={R.widget(chartType, item.i)} action='view' widgetName='Status'>
                  <GlWellEquipmentStatusWidget />
                </ProtectComponent>
              ) : (
                <NoAccessWidget widgetName='Status' />
              ))}
            {item.i === 'cardViewer' &&
              (isWidgetAccessible(item.moduleId) ? (
                <ProtectComponent resource={R.widget(chartType, item.i)} action='view' widgetName='Card viewer'>
                  <CardViewer
                    timeseriesChartData={timeseriesChartData}
                    layout={layout}
                    accessible={isWidgetAccessible(item.moduleId)}
                    externalChartRefs={sharedChartRefs}
                    onChartRegistered={registerContextualizerChart}
                  />
                </ProtectComponent>
              ) : (
                <NoAccessWidget widgetName='Card viewer' />
              ))}
          </div>
        ))}
      </GridLayout>
      {/* </div> */}
    </div>
  );
}
